<template>
  <div>
    <TopBar :page="topBarData" @menuItemClick="topMenuClicked"/>
    <v-container fluid>
      <v-row>
        <v-col>
          <EscalationAlert />
          <CreditControlAlert />
        </v-col>
      </v-row>
      
      <v-row>
        <v-col cols="12" lg="3">
          <AccountCardLoader v-if="currentAccountLoading" />
          <v-card v-if="!currentAccountLoading" class="d-flex align-stretch" height="100%">
            <v-card-text>
              <p class="mb-1 text-caption">Balance</p>
              <p class="display-1 accent--text">{{ getBalance | formatCurrency }}</p>

              <BaseLabelAndInfo class="px-0">
                <template #label>
                  Confirmed Date
                  <BaseInfoTooltip top>The date when the sale is closed</BaseInfoTooltip>
                </template>
                <template #value>
                  <p class="text-caption"><b>{{ currentAccount.confirmed || '-' }}</b></p>
                  <template v-if="currentAccount.tags!==null&&currentAccount.tags!==undefined">
                    <v-chip-group column>
                      <v-chip class="white--text" small v-for="(tag,ind) in currentAccount.tags" :key="ind" :color="tagColorMap(tag)">
                        {{ tag }}
                      </v-chip>
                    </v-chip-group>
                  </template>
                </template>
              </BaseLabelAndInfo>
            </v-card-text>
          </v-card>
        </v-col>
        <v-col cols="12" lg="3">
          <AccountCardLoader v-if="currentAccountLoading" />
          <v-card v-if="!currentAccountLoading" class="d-flex align-stretch" height="100%">
            <v-btn class="mt-0" absolute top right color="secondary" x-small @click="paymentMethodOpen=true;"><v-icon small>edit</v-icon></v-btn>
            <v-card-text>
              <BaseLabelAndInfo class="px-0">
                <template #label>
                  Billing Cycle
                  <BaseInfoTooltip top>The period between when the customer is invoiced and billed</BaseInfoTooltip>
                </template>
                <template #value>
                  <p class="text-caption"><b>{{ currentAccount.bill_cycle ? currentAccount.bill_cycle : "-" }}</b></p>
                </template>
              </BaseLabelAndInfo>
              <p class="mb-1 text-caption">Payment Method</p>
              <p><b>{{ currentAccount.payment_method ? `${currentAccount.payment_method}` : "-"}}</b></p>
              <p class="mb-1 text-caption">Bank Details</p>
              <p><b>{{sort_code_account_num}}</b></p>

              <div class="mb-3">
                <template v-if="currentAccount.payee_confirm_status">
                  <v-chip small :color="copStatusMap[currentAccount.payee_confirm_status]" class="mr-2 mb-2">
                    <span class="font-weight-bold">CoP:&nbsp;</span> {{ currentAccount.payee_confirm_status }}
                  </v-chip>
                </template>

                <template v-else>
                  <v-chip small color="grey lighten-1" class="white--text mr-2 mb-2">
                    <span>No CoP</span>
                  </v-chip>
                </template>

                <v-chip small color="grey darken-2" class="white--text mb-2">
                  <span class="font-weight-bold">Account Type:&nbsp;</span> {{ currentAccount.payee_account_type | capitalizeString | customPlaceholder }}
                </v-chip>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
        <v-col cols="12" lg="3">
          <AccountCardLoader v-if="currentAccountLoading" />
          <v-card v-if="!currentAccountLoading" class="d-flex align-stretch" height="100%">
            <v-btn class="mt-0" absolute top right color="secondary" x-small @click="billingContactOpen=true;"><v-icon small>edit</v-icon></v-btn>
            <v-card-text>
              <p class="mb-1 text-caption">Billing Contact</p>
              <p><b>
                {{currentAccount.first_name | customPlaceholder}} {{currentAccount.last_name | customPlaceholder}} <br>
                {{ currentAccount.email_address | customPlaceholder}} <br>
                {{ formattedPhoneNumber ? formattedPhoneNumber : currentAccount.phone_number }} <br>
                {{ formattedMobileNumber ? formattedMobileNumber : currentAccount.mobile_number }}
              </b></p>
              <template v-if="currentAccount.authorised_contact.email_address!==undefined">
                <p class="mb-1 text-caption">Authorised Contact</p>
                <p><b>
                  {{currentAccount.authorised_contact.name | customPlaceholder}} <br>
                  {{ currentAccount.authorised_contact.email_address | customPlaceholder}} <br>
                  {{ currentAccount.authorised_contact.phone_number | customPlaceholder}} <br>
                  {{ currentAccount.authorised_contact.mobile_number | customPlaceholder}}
                </b></p>
              </template>
            </v-card-text>
          </v-card>
        </v-col>
        <v-col cols="12" lg="3">
          <AccountCardLoader v-if="currentAccountLoading" />
          <v-card v-if="!currentAccountLoading" class="d-flex align-stretch" height="100%">
            <v-btn class="mt-0" absolute top right color="secondary" x-small @click="billingAddressOpen=true;"><v-icon small>edit</v-icon></v-btn>
            <v-card-text>
              <p class="mb-1 text-caption">Business Name</p>
              <p><b>{{currentAccount.business_name }}</b></p>
              <p class="mb-1 text-caption">Billing Address</p>
              <p><b>
                {{currentAccount.address_line_one ? currentAccount.address_line_one.split(' ').map(str => capitalizeFirstLetter(str)).join(' ') : ''}} <br>
                {{currentAccount.address_line_two ? currentAccount.address_line_two.split(' ').map(str => capitalizeFirstLetter(str)).join(' ') : ''}} <br>
                {{currentAccount.address_city ? currentAccount.address_city.split(' ').map(str => capitalizeFirstLetter(str)).join(' ') : ''}} <br>
                {{ currentAccount.address_post_code }}
              </b></p>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="12" md="4">
          <Notes :id="$route.params.id" notesType="accounts" />
        </v-col>
        <v-col cols="12" md="8">
          <v-row class="mt-n3 mb-n3">
            <v-col cols="12" md="12">
              <ProductInfo :accountId="$route.params.id" :height="height" />
            </v-col>
            <v-col cols="12" md="6">
              <RetentionHistory :id="$route.params.id" :heading="true" :width="'100%'" @open-create="openCreateRetentionLead"/>
            </v-col>
            <v-col cols="12" md="6">
              <CreditNotesHistory />
            </v-col>
          </v-row>
        </v-col>
      </v-row>

      <v-row justify="center">
        <v-dialog v-model="alert" persistent max-width="350">
          <v-card>
            <v-card-title>{{buttonActionText}}</v-card-title>
            <v-card-text>Are you sure you want to {{ confirmationText ? confirmationText : buttonActionText.toLowerCase() }}?</v-card-text>
            <v-divider></v-divider>
            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn color="primary" small :id="buttonActionId" :loading="alertDialogLoader" :disabled="alertDialogLoader" @click="onAlertOK">OK</v-btn>
              <v-btn color="primary" small outlined :disabled="alertDialogLoader" @click="closeAlert">Cancel</v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>
      </v-row>

      <CreateRetentionLead :dialogOpen="createRetentionLeadOpen" :products="products" :acc_id="currentAccount.reference" @close-dialog="closeCreateRetention"/>
      <EscalatePopup :open="escalatePopupOpen" :close="() => {escalatePopupOpen = false}" :value="escalatePopupOpen" :account="Number($route.params.id)" />
      <SendSMSPopup :open="sendSMSPopupOpen" :close="() => {sendSMSPopupOpen = false}" :value="sendSMSPopupOpen" :account="Number($route.params.id)" kind="account" :sub_kind="''" :phoneNumber="currentAccount.mobile_number || currentAccount.phone_number " />
      <Brochure :open="brochureOpen" :close="() => {brochureOpen = false}" :value="brochureOpen" :account="Number($route.params.id)" />
      <CreateProduct :open="createProductOpen" :close="() => setCreateProductOpen(false)" :value="createProductOpen" :account="Number($route.params.id)" />
      <BillingAddressPopup :open="billingAddressOpen" :close="() => {billingAddressOpen = false}" :value="billingAddressOpen" :account="currentAccount" @update-topbar="updateTopbar" />
      <BillingContactPopup :open="billingContactOpen" :close="() => {billingContactOpen = false}" :value="billingContactOpen" :account="currentAccount" />
      <PaymentMethodPopup :open="paymentMethodOpen" @close-dialog="paymentMethodOpen=false" :value="paymentMethodOpen" :account="currentAccount" />
      <DDMandateHistory :open="ddMandateHistoryOpen" @close-dialog="ddMandateHistoryOpen=false" :account="Number($route.params.id)" />

      <CreateCreditNoteProposal />
      <AccountAudit :dialogOpen="openAccountAudit" @close="openAccountAudit=false" />
    </v-container>
  </div>
</template>

<script>
import ProductInfo from "@/components/Products/ProductInfo";
import Brochure from "@/components/Misc/Brochure";
import EscalatePopup from "@/components/Misc/EscalatePopup";
import CreateProduct from "@/components/Accounts/CreateProduct";
import SendSMSPopup from "@/components/Misc/SendSMSPopup";
import PaymentMethodPopup from "@/components/Accounts/PaymentMethodPopup";
import BillingContactPopup from "@/components/Accounts/BillingContactPopup";
import BillingAddressPopup from "@/components/Accounts/BillingAddressPopup";
import CreateRetentionLead from "@/components/Accounts/CreateRetentionLead";
import AccountAudit from "@/components/Accounts/AccountAudit";
import Notes from "@/components/Globals/Notes/Notes";
import AccountCardLoader from "@/components/Misc/AccountCardLoader";
import Loader from "@/components/Misc/Loader";
import TopBar from "@/components/Layout/TopBar";
import EscalationAlert from "@/components/Accounts/Alerts/Escalation";
import CreditControlAlert from "@/components/Accounts/Alerts/CreditControl";
import RetentionHistory from "@/components/Retention/HistoryTable";
import CreditNotesHistory from "@/components/CreditNotes/CreditNotesHistory";
import DDMandateHistory from "@/components/Accounts/DDMandateHistoryPopup";
import CreateCreditNoteProposal from "@/components/CreditNotes/CreateCreditNoteProposal";

import { mapState, mapActions, mapMutations, mapGetters } from "vuex";
import { parsePhoneNumberFromString } from "libphonenumber-js";
import moment from "moment";

function baseProducts(vi){
  vi.products.forEach(p=>{
    if(p.ceased_date===null&&p.parent_product_id===null){
      vi.$set(vi.retentionCreate.form[2].products, p.id, {
        id:p.id,selected:false,transfer:false,transferDate:'',transferDateOpen:false,cancelReason:p.cancel_reason
      });
      p.contract_end_date = (p.contract_start_date!==null)?moment(p.contract_start_date).add(p.contract_length_months,'months').format('YYYY-MM-DD'):''
    }
  });
}

export default {
  data() {
    return {
      topBarData: {
        name: 'loading...',
        type:'account',
        menu:true,
        showSearch:true,
        breadcrumbs: [
          {
            text: 'Billing Accounts',
            disabled: false,
            href: '/accounts/',
          }
        ],
        showBeehiveIcon: true,
        articleLink: 'account',
      },
      createRetentionLeadOpen: false,
      account: {},
      alert: false,
      alertDialogLoader: false,
      buttonActionId: "",
      buttonActionText: "",
      confirmationText: '',
      kindNameMapping: {'web': 'Website','voip': 'VoIP','telco': 'Telco'},
      retentionCreate: {
        form: {
          1: {source:'',provider:'',department:'',notes:''},
          2: {products: {}},
        },
        sources: ['Incoming call','Email','Transfer request(attempts to be included)'],
        providers: ['8x8','BT','Cease','Chess','Daisy','Onebill','Plusnet','Talk Talk','XLN','Toople','Titan','Unicom','Kinex','Onebill','Virgin','SKY','Vodafone','Other','Rincentral','Vonage','CircleLoop','Soho66','Focus Group','VoIP Studio','Lili Comms','Gamma'].sort(),
        departments: ['Cancellations', 'Credit control','Front line','Faults','Onboarding','Sales','Provisioning','Gamma Transfer Out Request','BT/TT Transfer Out Request','VoIP Care','Incoming call IVR','Sales','ACM'].sort(),
        preCancelReasons: ['Business closed down','Service never worked','Moved into S/Office','Slow speeds, no fibre','Porting Issues','Order delay','Dropouts','Own phone setup issues','No follow up or call backs','No TT/BT Available','Broadcloud Issue','Fax line not needed','Left in error/Slammed','Password reset issue','Future live date not adhered to','Ported to bOnline VoIP','Ported to bOnine Telco','Business decision','Billing issues','Lack of functionality','Call Quality','Price matched/cheaper tariff','Mis-sold','24 support','Poor Service','Line ceased in error','Onboarding not done','Out of contract rates','Constant techical issues','Cancelled via email','Ombudsman case','Moved out of property','Other','Lease expired','Second line not needed','BB downtime','1 star review / Escalation','Refuse to TS','Slow speeds','Missold on speeds','Refused router cost','Ombudsman case','Business closed down','Business closed down (covid)','Terminal illness','Other','Fibre upgrade','New line installed with GP','OOC rates','Missold','COT/COP','Incorrect Billing','Price matched'].sort(),
        postCancelReasons: ['Lease issues','PAF issues','No customer updates','Missed Appointment','Missold','LP quarantined number','LP Price matched/Renewed','Transfer rejected/no contact to deal save','Other','LP rejecting transfer','Incorrect sale capture','Sold on cooling','No TT/BT Availability','Incompatible product on the line','Porting delays','Transfer rejected multiple times','Number cannot be ported','Cancelled during cooling off','No contact to deal save','Order delays','LP Price matched/cheaper tariff','Slow speeds','Missold on speeds','Refused router cost','Ombudsman case','Business closed down','Business closed down (covid)','Terminal illness','Other','Fibre upgrade','New line installed with GP','OOC rates','Missold','COT/COP','Incorrect Billing','Price matched'].sort(),
        proformaAmount: '',
        loading: true
      },
      brochureOpen: false,
      paymentMethodOpen: false,
      billingContactOpen: false,
      sendSMSPopupOpen: false,
      billingAddressOpen: false,
      escalatePopupOpen: false,
      queue_url: process.env.VUE_APP_QUEUE_URL || `https://queues-bonline-testing.appspot.com/`,
      ledger_url: process.env.VUE_APP_LEDGER_LINK || `https://ledger-test-bonline.appspot.com/`,
      openAccountAudit: false,
      ddMandateHistoryOpen: false,
      copStatusMap: {
        "Full Match": "secondary",
        "Partial Match": "warning",
        "No Match": "error",
      },
    };
  },
  async mounted() {
    await this.getAccountData();
    const parThis = this;
    let cont = true;
    let findingProducts = setInterval(()=>{
      if(cont){
        if(parThis.products.length!==0){
          baseProducts(parThis);
          cont = false;
        }
      }else{
        clearInterval(findingProducts);
      }
    },1000);
    let viewingAcc = setInterval(()=>{
      if(this.currentAccount.business_name!==undefined){
        this.topBarData.name = `${this.currentAccount.reference} - ${this.currentAccount.business_name}`;
        this.topBarData.breadcrumbs.push({
          text: this.topBarData.name,
          disabled: true,
          href: `/accounts/${this.currentAccount.reference}/`,
        });
        this.addRecentlyViewed({
          id: this.$route.params.id,
          name: this.currentAccount.business_name,
          type: 'accounts',
          status: '',
          datetime: moment(new Date()).format('YYYY-MM-DD HH:mm')
        });
        clearInterval(viewingAcc);
      }
    },500);
  },
  components: {
    ProductInfo,
    Brochure,
    PaymentMethodPopup,
    BillingContactPopup,
    BillingAddressPopup,
    Notes,
    EscalatePopup,
    RetentionHistory,
    CreditNotesHistory,
    CreateProduct,
    Loader,
    AccountCardLoader,
    TopBar,
    CreateRetentionLead,
    EscalationAlert,
    CreditControlAlert,
    CreateCreditNoteProposal,
    AccountAudit,
    SendSMSPopup,
    DDMandateHistory,
  },
  computed: {
    ...mapState("creditnotes", ["createProposalOpen"]),
    ...mapState("accounts", ["currentAccount","currentAccountLoading","accountEscalation","creditData","creditDataLoading"]),
    ...mapState("products", ["products", "createProductOpen"]),
    ...mapState("retention", ["createRetentionErrors",'retentionLeads']),
    ...mapGetters('comms', ['getContacts', 'getContactsError']),
    ...mapGetters('accounts', ['getResetCopError']),

    disabledButtonStep(){
      let step = this.createRetentionLeadStep;
      let data = this.retentionCreate.form[step];
      let disabled = true;
      if(step===1){
          if( data.source!=='' && data.provider!=='' && data.department!=='' ){ disabled = false; }
      }
      if(step===2){
          let prods = data.products;
          let reject = true;
          Object.keys(prods).forEach(pk=>{
            let p = prods[pk];
            if(!p.transfer || (p.transfer && p.transferDate!=='')){
              reject = false;
            }else { reject = true; }
          });
          disabled = reject;
      }
      if(step===3){
        disabled = false;
      }
      return disabled;
    },
    sort_code_account_num() {
      const bank_acc_no = this.currentAccount.bank_acc_no;
      const sort_code = this.currentAccount.sort_code;
      let masked_no = "";
      let masked_sc = "";
      if (bank_acc_no) {
        masked_no = bank_acc_no
          .split("")
          .map((n, i) => (i < bank_acc_no.length - 2 ? "*" : n))
          .join("");
      }
      if (sort_code) {
        masked_sc = sort_code
          .split("")
          .map((n, i) => (i < sort_code.length - 2 ? "*" : n))
          .join("");
      }
      return `${masked_sc} - ${masked_no}`;
    },
    formattedPhoneNumber() {
      const phoneNumber = parsePhoneNumberFromString(
        String(this.currentAccount.phone_number),
        "GB"
      );
      if (phoneNumber) {
        return phoneNumber.formatNational();
      }
      return false;
    },
    formattedMobileNumber() {
      const phoneNumber = parsePhoneNumberFromString(
        String(this.currentAccount.mobile_number),
        "GB"
      );
      if (phoneNumber) {
        return phoneNumber.formatNational();
      }
      return false;
    },
    height() {
      return this.escalation || this.exclusion ? 60 : 67;
    },
    ledgerLink() {
      return process.env.VUE_APP_LEDGER_LINK;
    },
    getBalance() {
      return this.currentAccount || this.creditData ? this.currentAccount.balance : this.creditData.balance;
    }
  },

  methods: {
    ...mapMutations("creditnotes", ["setCreateProposalOpen",]),
    ...mapActions("retention", ["createRetentionLead",]),
    ...mapActions("creditnotes", ["fetchCreditNoteReasonsByAccount",]),
    ...mapActions('globals', ['addRecentlyViewed']),
    ...mapActions("products", ["resetProductsState", "setCreateProductOpen"]),
    ...mapActions("alerts", ["setAlert"]),
    ...mapActions("accounts", [
      "getCurrentAccount",
      "getAccountNotes",
      "getAccountEscalation",
      "removeAccountEscalation",
      "sendPaymentLinkEmail",
      "sendPaymentLinkSMS",
      "sendAccStatement",
      "getCreditData",
      "getCRMUsers",
      "sendDDMandate",
      "sendContactRequest",
      "sendPortalInvite",
      "resetAccountState",
      'resetCop'
    ]),
    ...mapActions('comms', ['fetchContacts']),

    tagColorMap(tag){
      switch(tag){
        case 'Veteran': return 'primary';
        case 'Loyal': return 'tertiary';
        case 'High Value': return 'warning';
        case 'Multi Product': return 'secondary';
      }
    },
    updateTopbar(){
      this.topBarData.breadcrumbs.pop();
      let viewingAcc = setInterval(()=>{
        if(this.currentAccount.business_name!==undefined){
          this.topBarData.name = `${this.currentAccount.reference} - ${this.currentAccount.business_name}`;
          this.topBarData.breadcrumbs.push({
            text: this.topBarData.name,
            disabled: true,
            href: `/accounts/${this.currentAccount.reference}/`,
          });
          this.addRecentlyViewed({
            id: this.$route.params.id,
            name: this.currentAccount.business_name,
            type: 'accounts',
            status: '',
            datetime: moment(new Date()).format('YYYY-MM-DD HH:mm')
          });
          clearInterval(viewingAcc);
        }
      },500);
    },
    closeCreateRetention(){
      this.createRetentionLeadOpen = false;
    },
    async getAccountData() {
      const accId = this.$route.params.id;
      await this.getCurrentAccount(accId);
      this.fetchCreditNoteReasonsByAccount(accId);
      this.getAccountEscalation(accId);
      this.getCreditData(accId);
      this.getCRMUsers();
    },
    close() {
      this.$router.go(-1);
    },
    async onAlertOK(e) {
      this.alertDialogLoader = true;
      switch (e.target.parentElement.id) {
        case "alert-paymentLink":
          await this.sendPaymentLinkEmail(this.currentAccount.reference);
          this.alert = false;
          break;
        case "alert-paymentLinkSMS":
          await this.sendPaymentLinkSMS(this.currentAccount.id);
          this.alert = false;
          break;
        case "alert-sendAccStatement":
          await this.sendAccStatement(this.currentAccount.reference);
          this.alert = false;
          break;
        case "alert-DDMandate":
          await this.sendDDMandate(this.currentAccount.reference);
          this.alert = false;
          break;
        case "alert-portalInvitation":
          await this.sendPortalInvite(this.currentAccount.reference);
          this.alert = false;
          break;
        case "alert-sendCR":
          await this.sendContactRequest(this.currentAccount.reference);
          this.alert = false;
          break;
        case 'alert-resetCop':
          await this.resetCop(this.currentAccount.id);
          if (!this.getResetCopError) {
            this.getCurrentAccount(this.currentAccount.id);
            this.alert = false;
          }
          break;        
      }

      if (!this.alert) {
        this.resetAlert();
      }
      this.alertDialogLoader = false;
    },
    
    topMenuClicked(menuItem){
      switch (menuItem) {
        case "goToCreditControl":
          this.$router.push(`/accounts/${this.$route.params.id}/credit-control`);
          break;
        case "showAccountAudit":
          this.openAccountAudit = true;
          break;
        case "addCreditNoteProposal":
          this.setCreateProposalOpen(true);
          break;
        case "takePayment":
          window.open(`${this.ledger_url}payments/${this.currentAccount.id}?email=${encodeURIComponent(this.currentAccount.email_address)}`, "_blank");
          break;
        case "sendPaymentLink":
          this.buttonActionId = "alert-paymentLink";
          this.buttonActionText = "Send Payment Link";
          this.alert = true;
          break;
        case "sendPaymentLinkSMS":
          this.buttonActionId = "alert-paymentLinkSMS";
          this.buttonActionText = "Send Payment Link SMS";
          this.alert = true;
          break;
        case "sendAccountStatement":
          this.buttonActionId = "alert-sendAccStatement";
          this.buttonActionText = "Send Account Statement";
          this.alert = true;
          break;
        case "sendDirectDebitMandate":
          this.buttonActionText = "Send Direct Debit Mandate";
          this.buttonActionId = "alert-DDMandate";
          this.alert = true;
          break;
        case "directDebitMandateHistory":
          this.ddMandateHistoryOpen = true;
          break;
        case "sendContactRequestEmail":
          this.buttonActionText = "Send Contact Request Email";
          this.buttonActionId = "alert-sendCR";
          this.alert = true;
          break;
        case "sendSMS":
          this.sendSMSPopupOpen = true;
          break;
        case "escalate":
          this.escalatePopupOpen = true;
          break;
        case "sendPortalInvitation":
          this.buttonActionText = "Send Portal Invitation";
          this.buttonActionId = "alert-portalInvitation";
          this.alert = true;
          break;
        case "sendBrochure":
          this.brochureOpen = true;
          break;
        case "goToLedger":
          window.open(`${this.ledgerLink}transactions/${this.currentAccount.reference}`);
          break;
        case "billingQueue":
          window.open(`${this.queue_url}billing/actions/?account_id=${this.currentAccount.id}`);
          break;
        case 'comms':
            this.goToComms();
            break;
        case 'resetCop':
          this.buttonActionText = 'Reset COP';
          this.confirmationText = 'reset the COP status of this account';
          this.buttonActionId = 'alert-resetCop';
          this.alert = true;
          break;
      }
    },

    async goToComms() {
      const data = {type: 'account', id: this.currentAccount.id};     
      await this.fetchContacts(data);

        if (!this.getContactsError) {
          const emails = [...new Set(this.getContacts.map( contact =>  { 
            if (contact.email_address) { return contact.email_address}
          }))];
          const phone_numbers = [...new Set(this.getContacts.map( contact =>  {
            if (contact.phone_number) { return contact.phone_number}
          }))];

          localStorage.setItem('comms_emails', JSON.stringify(emails));
          localStorage.setItem('comms_numbers', JSON.stringify(phone_numbers));

          window.open('/comms/');
        }
    },

    openCreateRetentionLead() {
      this.createRetentionLeadOpen = true;
    },
    capitalizeFirstLetter(str) {
      if (str) {
        return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
      }
      return str;
    },
    moment(date) {
      return moment(date);
    },

    resetAlert() {
      this.buttonActionText = '';
      this.confirmationText = '';
      this.buttonActionId = '';
    },

    closeAlert() {
      this.resetAlert();
      this.alert = false;
    }
  },

  beforeRouteEnter(to, from, next) {
    next(vm => {
      vm.from = from;
    });
  },
  
  beforeRouteLeave(to, from, next) {
    this.resetAccountState();
    this.resetProductsState();
    next();
  },
};
</script>
<style lang="scss" scoped>
.v-data-table ::v-deep tbody tr {
  cursor: pointer !important;
}
.v-stepper {
  box-shadow: none !important;
  .v-stepper__header {
    box-shadow: none !important;
  }
  .v-stepper__content {
    padding: 0;
  }
}
table .v-input__control .v-input__slot {
  padding: 0 !important;
  margin-right: -8px !important;
}
.menu-wrapper {
  color: rgba(0, 0, 0, 0.54);
  display: flex;
  justify-content: flex-end;
}
.v-btn {
  color: rgba(0, 0, 0, 0.54);
  padding: auto 3rem;
  margin: 1rem 0rem;
}
.no-border {
  border: none !important;
}

.grey {
  color: rgba(0, 0, 0, 0.54);
}

.edit {
  font-size: 18px;
  &:hover {
    cursor: pointer;
  }
}
a {
  text-decoration: none;
}

::v-deep .notes__list {  
  max-height: 250px;
}
</style>
